import React, { Component } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { signup } from "../auth";
import { Link } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Link as MuiLink
} from '@mui/material';
import Footer from '../core/Footer';

class Signup extends Component { 
  constructor() {
    super();
    this.state = {
      name: "",
      lastName: '',
      companyName: '',
      companyType: '',
      email: "",
      password: "",
      error: "",
      success: false,
      recaptcha: false,
      terms: false
    };
  }

  adminMessage = ''

  handleChange = (name) => (event) => {
    const value =
      name === "terms"
        ? event.target.checked ? "on" : ""
        : event.target.value;

    this.setState({ [name]: value, error: "" });
  };

  recaptchaRef = React.createRef();

  onRecaptchaChange = (token) => {
    this.setState({ recaptcha: !!token, recaptchaToken: token || "" });
  };

  onRecaptchaError = () => {
    this.setState({ recaptchaError: true });
  };

  clickSubmit = async (event) => {
    event.preventDefault();

    const { name, lastName, companyName, companyType, email, password, terms, recaptcha } = this.state;

    if (!recaptcha) return this.setState({ error: "Please complete the reCAPTCHA verification." });
    if (terms !== "on") {
      return this.setState({ error: "You must accept the terms." });
    }
    if (/gmail/i.test(email)) {
      return this.setState({
        error: "We do not accept Gmail addresses at this time. Please use your company e-mail."
      });
    }

    const user = { name, lastName, companyName, companyType, email: email.toLowerCase(), password, terms, recaptchaToken: this.state.recaptchaToken };

    try {
      const data = await signup(user);
      if (data.error) return this.setState({ error: data.error });

      // Confirmation email is now sent server-side during signup

      this.setState({
        success: true,
        error: "",
        name: "",
        lastName: "",
        companyName: "",
        companyType: "",
        email: "",
        password: "",
        terms: false,
        recaptcha: false
      });
    } catch (err) {
      console.error(err);
      this.setState({ error: "Something went wrong. Please try again later." });
    }
  };

  render() {
    const { name, lastName, companyName, companyType, email, password, terms, recaptcha, error, success } = this.state;

    return (
      <>
        <Container maxWidth="sm" sx={{ mt: 5, mb: 5 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
              Sign Up
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Account created successfully! Please{' '}
                <MuiLink component={Link} to="/signin" underline="hover">
                  sign in
                </MuiLink>
                .
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
              Registration is 100% free. Already have an account?{' '}
              <MuiLink component={Link} to="/signin" underline="hover">
                Sign in
              </MuiLink>
            </Alert>

            {!success && (
              <Box component="form" onSubmit={this.clickSubmit}>
                <TextField
                  fullWidth
                  label="First Name"
                  type="text"
                  value={name}
                  onChange={this.handleChange("name")}
                  margin="normal"
                  variant="outlined"
                  required
                  autoComplete="given-name"
                  inputProps={{ style: { fontSize: '16px' } }}
                />

                <TextField
                  fullWidth
                  label="Last Name"
                  type="text"
                  value={lastName}
                  onChange={this.handleChange("lastName")}
                  margin="normal"
                  variant="outlined"
                  required
                  autoComplete="family-name"
                  inputProps={{ style: { fontSize: '16px' } }}
                />

                <TextField
                  fullWidth
                  label="Company Name"
                  type="text"
                  value={companyName}
                  onChange={this.handleChange("companyName")}
                  margin="normal"
                  variant="outlined"
                  required
                  autoComplete="organization"
                  inputProps={{ style: { fontSize: '16px' } }}
                />

                <FormControl fullWidth margin="normal" variant="outlined" required>
                  <InputLabel>Company Type</InputLabel>
                  <Select
                    value={companyType}
                    onChange={this.handleChange('companyType')}
                    label="Company Type"
                  >
                    <MenuItem value="">
                      <em>-- Select company type --</em>
                    </MenuItem>
                    <MenuItem value="Utility">Utility Company</MenuItem>
                    <MenuItem value="Services">Engineering Services Provider</MenuItem>
                    <MenuItem value="Vendor">Vendor</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={this.handleChange("email")}
                  margin="normal"
                  variant="outlined"
                  required
                  autoComplete="email"
                  inputProps={{ style: { fontSize: '16px' } }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={this.handleChange("password")}
                  margin="normal"
                  variant="outlined"
                  required
                  autoComplete="new-password"
                  inputProps={{ style: { fontSize: '16px' } }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={terms === "on"}
                      onChange={this.handleChange("terms")}
                      name="terms"
                      required
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I agree to the{' '}
                      <MuiLink href="/privacystatement" target="_blank" underline="hover">
                        SDSIC privacy policies
                      </MuiLink>
                      {' '}and the{' '}
                      <MuiLink href="/termsstatement" target="_blank" underline="hover">
                        SDSIC website Terms of Service
                      </MuiLink>
                    </Typography>
                  }
                  sx={{ mt: 2, mb: 2, alignItems: 'flex-start' }}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 2, '& > div': { maxWidth: '100%', overflow: 'hidden' } }}>
                  <ReCAPTCHA
                    ref={this.recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={this.onRecaptchaChange}
                    onErrored={this.onRecaptchaError}
                    size={window.innerWidth < 400 ? 'compact' : 'normal'}
                  />
                  {this.state.recaptchaError && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      reCAPTCHA failed to load. Please disable content blockers or try a different browser.
                    </Alert>
                  )}
                </Box>

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ mt: 3 }}
                >
                  Submit
                </Button>
              </Box>
            )}
          </Paper>
        </Container>
        <Footer />
      </>
    );
  }
}

export default Signup;
